import { __rest } from '../node_modules/.pnpm/@rollup_plugin-typescript@11.1.6_rollup@4.65.1_tslib@2.6.2_typescript@5.9.3/node_modules/tslib/tslib.es6.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useReactTable, functionalUpdate, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { clsx } from 'clsx';
import { useRef, useState } from 'react';
import { InputText } from '../InputText/InputText.js';
import { Text } from '../Text/Text.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import styles from './DataTablePrototype.module.css.js';
import { autoColumnSize } from './autoColumnSize.js';
import { getSortIcon } from './getSortIcon.js';
import { getSortTitle } from './getSortTitle.js';

function DataTablePrototype(_a) {
    // TODO: would be nice to have a hook that can handle being controlled or uncontrolled for state.
    // Something like this but with TS: https://github.com/quid/refraction/blob/master/packages/react-use-controlled-state/src/index.js
    // const [sorting, setSorting] = useState<SortingState>([]);
    // const [globalFilter, setGlobalFilter] = useState("");
    var { showGlobalFilter: showFilters = true, maxHeight } = _a, props = __rest(_a, ["showGlobalFilter", "maxHeight"]);
    return (jsxs(Fragment, { children: [showFilters ?
                jsx(InputText, { name: "filter", placeholder: "Filter", value: props.globalFilter, onChange: (newGlobalFilter) => {
                        var _a;
                        (_a = props.onGlobalFilterChange) === null || _a === void 0 ? void 0 : _a.call(props, newGlobalFilter);
                    } })
                : null, jsx(InternalTable, Object.assign({}, props, { maxHeight: maxHeight }))] }));
}
// Split the internal table into its own component to avoid re-renders when the
// global filter changes.
function InternalTable({ columns, getRowId, data, columnVisibility, sorting = [], onSortingChange, globalFilter, onGlobalFilterChange, maxHeight, }) {
    const table = useReactTable({
        columns,
        data: data,
        getRowId: (originalRow, index) => { var _a; return (_a = getRowId === null || getRowId === void 0 ? void 0 : getRowId(originalRow)) !== null && _a !== void 0 ? _a : index.toString(); },
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        onGlobalFilterChange,
        onSortingChange: (newSorting) => {
            onSortingChange === null || onSortingChange === void 0 ? void 0 : onSortingChange(functionalUpdate(newSorting, sorting));
        },
        state: {
            columnVisibility,
            sorting,
            globalFilter,
        },
    });
    return (jsx("div", { className: styles.wrapper, style: maxHeight ? { maxHeight } : undefined, children: jsxs("table", { className: styles.DataTable, children: [jsx("thead", { className: styles.thead, children: table.getHeaderGroups().map((headerGroup) => (jsx("tr", { children: headerGroup.headers.map((header) => {
                            const content = flexRender(header.column.columnDef.header, header.getContext());
                            const textProps = {
                                weight: "bold",
                                align: "start",
                            };
                            const thStyles = {
                                width: header.getSize() === autoColumnSize.size ?
                                    "auto"
                                    : header.getSize(),
                            };
                            if (!header.column.getCanSort()) {
                                return (jsx("th", { colSpan: header.colSpan, className: styles.headingLabel, style: thStyles, children: jsx(Text, Object.assign({}, textProps, { children: content })) }, header.id));
                            }
                            return (jsx("th", { colSpan: header.colSpan, className: styles.heading, style: thStyles, children: header.isPlaceholder ? null : (jsx("button", { type: "button", className: clsx(styles.headingLabel, styles.sortable), onClick: header.column.getToggleSortingHandler(), title: getSortTitle(header.column.getNextSortingOrder()), children: jsxs(Text, Object.assign({}, textProps, { children: [content, " ", getSortIcon(header.column.getIsSorted())] })) })) }, header.id));
                        }) }, headerGroup.id))) }), jsx("tbody", { children: table.getRowModel().rows.map((row) => (jsx("tr", { children: row.getVisibleCells().map((cell) => (jsx("td", { className: styles.cell, children: jsx(CellContent, { children: flexRender(cell.column.columnDef.cell, cell.getContext()) }) }, cell.id))) }, row.id))) }), jsx("tfoot", { children: table.getFooterGroups().map((footerGroup) => (jsx("tr", { children: footerGroup.headers.map((header) => (jsx("th", { children: header.isPlaceholder ? null : (jsx(Text, { children: flexRender(header.column.columnDef.footer, header.getContext()) })) }, header.id))) }, footerGroup.id))) })] }) }));
}
function CellContent({ children }) {
    const ref = useRef(null);
    const [tooltipText, setTooltipText] = useState("");
    const handleMouseEnter = () => {
        var _a;
        const content = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.firstElementChild;
        if (content) {
            const isTruncated = content.scrollWidth > content.clientWidth;
            setTooltipText(isTruncated ? content.textContent : "");
        }
    };
    return (jsx(Tooltip, { text: tooltipText, disabled: !tooltipText, block: true, children: jsx("span", { className: styles.cellContent, onMouseEnter: handleMouseEnter, ref: ref, children: jsx(Text, { truncate: true, children: children }) }) }));
}

export { DataTablePrototype };
//# sourceMappingURL=DataTablePrototype.js.map
